import React from 'react';
import { translate } from 'react-i18next';
import { Button, ButtonGroup } from 'react-bootstrap';
import Constants from '../../config/constants';

/***
 * Handle zooming and rotation
 */
const ZOOM_REPEAT_DELAY_MS = 400;
const ZOOM_REPEAT_INTERVAL_MS = 80;

export class EditorCanvas extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      changeAngleMode: false,
      groupAngleX: Constants.SC_EDITOR_GROUP_ANGLE_X,
      groupAngleY: Constants.SC_EDITOR_GROUP_ANGLE_Y,
      zoomLevel: Constants.SC_EDITOR_ZOOM,
    };

    this.zoomRepeatTimeout = null;
    this.zoomRepeatInterval = null;
  }

  componentWillUnmount() {
    this.stopZoomRepeat();
  }

  onAngleBegin(x, y) {
    this.setState({
      ...this.state,
      changeAngleMode: true,
      startX: x,
      startY: y,
    });
  }

  onAngleEnded() {
    this.setState({
      ...this.state,
      changeAngleMode: false,
      startX: null,
      startY: null,
    });
  }

  onAngleChanged(x, y) {
    if (this.state.changeAngleMode) {
      const newGroupAngleY = this.state.groupAngleY + (x - this.state.startX) * Constants.SC_EDITOR_SCALE_ANGLE;
      const newGroupAngleX = this.state.groupAngleX + (y - this.state.startY) * Constants.SC_EDITOR_SCALE_ANGLE;
      this.setState({
        groupAngleX: Math.round(Math.min(Math.max(newGroupAngleX, Constants.SC_EDITOR_ANGLE_X_MIN), Constants.SC_EDITOR_ANGLE_X_MAX)),
        groupAngleY: Math.round(Math.min(Math.max(newGroupAngleY, Constants.SC_EDITOR_ANGLE_Y_MIN), Constants.SC_EDITOR_ANGLE_Y_MAX)),
      });
    }
  }

  onZoomLevelChanged(deltaZoomLevel) {
    const newZoomLevel = this.state.zoomLevel + deltaZoomLevel * Constants.SC_EDITOR_SCALE_ZOOM;
    this.setState({
      zoomLevel: Math.min(Math.max(newZoomLevel, Constants.SC_EDITOR_ZOOM_MIN), Constants.SC_EDITOR_ZOOM_MAX),
    });
  }

  stopZoomRepeat() {
    if (this.zoomRepeatTimeout) {
      clearTimeout(this.zoomRepeatTimeout);
      this.zoomRepeatTimeout = null;
    }
    if (this.zoomRepeatInterval) {
      clearInterval(this.zoomRepeatInterval);
      this.zoomRepeatInterval = null;
    }
  }

  startZoomRepeat(delta) {
    this.stopZoomRepeat();
    this.onZoomLevelChanged(delta);

    this.zoomRepeatTimeout = setTimeout(() => {
      this.zoomRepeatInterval = setInterval(() => {
        const { zoomLevel } = this.state;
        const atLimit = delta < 0
          ? zoomLevel <= Constants.SC_EDITOR_ZOOM_MIN
          : zoomLevel >= Constants.SC_EDITOR_ZOOM_MAX;

        if (atLimit) {
          this.stopZoomRepeat();
          return;
        }

        this.onZoomLevelChanged(delta);
      }, ZOOM_REPEAT_INTERVAL_MS);
    }, ZOOM_REPEAT_DELAY_MS);
  }

  getZoomButtonHandlers(delta) {
    const canZoom = delta < 0
      ? this.state.zoomLevel > Constants.SC_EDITOR_ZOOM_MIN
      : this.state.zoomLevel < Constants.SC_EDITOR_ZOOM_MAX;

    return {
      onMouseDown: (e) => {
        e.preventDefault();
        if (canZoom) {
          this.startZoomRepeat(delta);
        }
      },
      onMouseUp: () => this.stopZoomRepeat(),
      onMouseLeave: () => this.stopZoomRepeat(),
      onTouchStart: (e) => {
        e.preventDefault();
        if (canZoom) {
          this.startZoomRepeat(delta);
        }
      },
      onTouchEnd: () => this.stopZoomRepeat(),
      onTouchCancel: () => this.stopZoomRepeat(),
    };
  }

  renderViewControls() {
    const { t } = this.props;
    const { groupAngleX, groupAngleY, zoomLevel, viewMode } = this.state;
    const displayZoom = Math.round((zoomLevel - Constants.SC_EDITOR_ZOOM_MIN) / Constants.SC_EDITOR_ZOOM_STEP) + 1;

    return (
      <div className="canvas-view-controls">
        {/* View Presets */}
        <div className="control-group">
          <label className="control-label">{t('view')}</label>
          <div className="preset-buttons">
            <button
              className={`preset-btn ${groupAngleX === 0 && groupAngleY === 0 ? 'active' : ''}`}
              onClick={() => this.setState({ groupAngleX: 0, groupAngleY: 0 })}
              title={t('front')}
            >
              <i className="fas fa-square" />
              <span>{t('front')}</span>
            </button>
            <button
              className={`preset-btn ${groupAngleX === 0 && groupAngleY === Constants.SC_EDITOR_ANGLE_Y_MAX ? 'active' : ''}`}
              onClick={() => this.setState({ groupAngleX: 0, groupAngleY: Constants.SC_EDITOR_ANGLE_Y_MAX })}
              title={t('left')}
            >
              <i className="fas fa-caret-square-left" />
              <span>{t('left')}</span>
            </button>
            <button
              className={`preset-btn ${groupAngleX === 0 && groupAngleY === Constants.SC_EDITOR_ANGLE_Y_MIN ? 'active' : ''}`}
              onClick={() => this.setState({ groupAngleX: 0, groupAngleY: Constants.SC_EDITOR_ANGLE_Y_MIN })}
              title={t('right')}
            >
              <i className="fas fa-caret-square-right" />
              <span>{t('right')}</span>
            </button>
            <button
              className={`preset-btn ${groupAngleX === Constants.SC_EDITOR_GROUP_ANGLE_X && groupAngleY === Constants.SC_EDITOR_GROUP_ANGLE_Y ? 'active' : ''}`}
              onClick={() => this.setState({ groupAngleX: Constants.SC_EDITOR_GROUP_ANGLE_X, groupAngleY: Constants.SC_EDITOR_GROUP_ANGLE_Y })}
              title={t('isometric')}
            >
              <i className="fas fa-cube" />
              <span>{t('isometric')}</span>
            </button>
            <button
              className={`preset-btn ${groupAngleX === Constants.SC_EDITOR_ANGLE_X_MIN && groupAngleY === 0 ? 'active' : ''}`}
              onClick={() => this.setState({ groupAngleX: Constants.SC_EDITOR_ANGLE_X_MIN, groupAngleY: 0 })}
              title={t('top')}
            >
              <i className="fas fa-arrow-down" />
              <span>{t('top')}</span>
            </button>
          </div>
        </div>

        {/* Angle Controls */}
        <div className="control-group">
          <label className="control-label">{t('angle')}</label>
          <div className="angle-controls">
            <div className="angle-item">
              <span className="angle-label">X</span>
              <input
                type="range"
                min={Constants.SC_EDITOR_ANGLE_X_MIN}
                max={Constants.SC_EDITOR_ANGLE_X_MAX}
                value={groupAngleX}
                onChange={e => this.setState({ groupAngleX: parseInt(e.target.value, 10) })}
              />
              <span className="angle-value">{groupAngleX}°</span>
            </div>
            <div className="angle-item">
              <span className="angle-label">Y</span>
              <input
                type="range"
                min={Constants.SC_EDITOR_ANGLE_Y_MIN}
                max={Constants.SC_EDITOR_ANGLE_Y_MAX}
                value={groupAngleY}
                onChange={e => this.setState({ groupAngleY: parseInt(e.target.value, 10) })}
              />
              <span className="angle-value">{groupAngleY}°</span>
            </div>
          </div>
        </div>

        {/* Zoom Control */}
        <div className="control-group">
          <label className="control-label">{t('zoom level')}</label>
          <div className="zoom-controls">
            <button
              type="button"
              className="zoom-btn"
              disabled={zoomLevel <= Constants.SC_EDITOR_ZOOM_MIN}
              {...this.getZoomButtonHandlers(-1)}
            >
              <i className="fas fa-minus" />
            </button>
            <span className="zoom-value">{displayZoom}</span>
            <button
              type="button"
              className="zoom-btn"
              disabled={zoomLevel >= Constants.SC_EDITOR_ZOOM_MAX}
              {...this.getZoomButtonHandlers(1)}
            >
              <i className="fas fa-plus" />
            </button>
          </div>
        </div>

      </div>
    );
  }

  render() {
    const { groupAngleX, groupAngleY, zoomLevel } = this.state;
    const { width, height } = this.props;

    return (
      <div className="editor-canvas-container">
        {/* Left side - View Controls */}
        {this.renderViewControls()}
        
        {/* Right side - 3D Canvas */}
        <div
          className="sc-wrapper"
          onMouseDown={({ clientX, clientY }) => this.onAngleBegin(clientX, clientY)}
          onMouseMove={({ clientX, clientY }) => this.onAngleChanged(clientX, clientY)}
          onMouseUp={() => this.onAngleEnded()}
          onWheel={({ deltaY }) => this.onZoomLevelChanged(deltaY)}
          onTouchStart={({ targetTouches }) => targetTouches.length === 1 && this.onAngleBegin(targetTouches[0].clientX, targetTouches[0].clientY)}
          onTouchMove={({ targetTouches }) => this.onAngleChanged(targetTouches[0].clientX, targetTouches[0].clientY)}
          onTouchEnd={() => this.onAngleEnded()}
        >
          <div
            className="group"
            style={{
              transform: `rotateX(${groupAngleX}deg) rotateY(${groupAngleY}deg)`,
              fontSize: `${zoomLevel}em`,
              width: `${width}em`,
              height: `${height}em`,
            }}
          >
            {this.props.children}
            <div
              className="axis axis-z"
              style={{
                width: `${width}em`,
                height: `${height * 2}em`,
              }}
            >
              <label
                style={{
                  transform: `rotateY(${180 - groupAngleY}deg) rotateX(${groupAngleX}deg)`,
                }}>Z</label>
            </div>
            <div
              className="axis axis-x"
              style={{
                width: `${width}em`,
                height: `${height * 2}em`,
                transform: `translateZ(${height}em) translateY(${-height}em) rotateX(90deg)`,
              }}
            >
              <label
                style={{
                  transform: `rotateX(${90 - groupAngleX}deg) rotateY(${groupAngleY}deg)`,
                }}>X</label>
            </div>
            <div
              className="axis axis-y"
              style={{
                width: `${width * 2}em`,
                height: `${height}em`,
              }}
            >
              <label
                style={{
                  transform: `rotateX(${180 - groupAngleX}deg) rotateY(${groupAngleY}deg)`,
                }}>Y</label>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

EditorCanvas.defaultProps = {
  showIndicator: false,
  showPresets: false,
  width: 16,
  height: 8,
};

export default translate('translations')(EditorCanvas);



// WEBPACK FOOTER //
// ./src/components/sc_editor/EditorCanvas.js